/* One sign-in, shared with every other Jackdesk app.
 *
 * A code to a work address, never a password — and never a second account for
 * the ERP. Three things here are deliberate and were each learned somewhere
 * else in the product:
 *
 *  - **A code still valid is offered, not silently replaced.** Sending a new
 *    one invalidates the one already in their inbox, which is maddening when
 *    they are looking straight at it.
 *  - **Whether the ERP is theirs to use is asked before the code is sent.**
 *    jackdesk_auth is shared and is never told which app is asking, so it
 *    would email a code to somebody who can only land on "no workspace".
 *  - **Refusals say what to do next**, and name a person to ask, because the
 *    reader cannot fix any of them alone.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'
import { consumeReturnTo } from '@jackdesk/shell'
import { AuthError, checkEmail, mayUseErp, requestCode, verifyCode } from './auth'
import { APP_NAME } from './config'

/** What jackdesk_auth's and the gateway's stable codes mean to a person. */
const SOMETHING_WENT_WRONG = 'Something went wrong.'

const REASONS: Record<string, string | undefined> = {
  no_account: 'There is no Jackdesk account for that address. Ask your organisation’s owner to add you.',
  no_erp_workspace: 'That address cannot open the ERP yet. Ask your organisation’s owner to give you access.',
  account_inactive: 'That account is switched off. Ask your organisation’s owner.',
}

export function SignIn() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  // 'choose': a code emailed earlier is still valid, and they pick whether to
  // use it or have a new one sent — rather than the app deciding for them.
  const [step, setStep] = useState<'email' | 'choose' | 'code'>('email')
  const [minutesLeft, setMinutesLeft] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [note, setNote] = useState('')

  const fail = (e: unknown) =>
    setError(e instanceof AuthError
      ? ((e.code && REASONS[e.code]) || e.message)
      : SOMETHING_WENT_WRONG)

  async function sendCode(event: FormEvent) {
    event.preventDefault(); setError(''); setBusy(true)
    try {
      const address = email.trim()
      const status = await checkEmail(address)
      if (!status.user_exists) { setError(REASONS.no_account!); return }
      const allowed = await mayUseErp(address)
      if (!allowed.allowed) { setError(REASONS[allowed.reason] ?? REASONS.no_erp_workspace!); return }
      if (status.has_valid_otp) {
        setMinutesLeft(status.validity_remaining_minutes ?? 0)
        setStep('choose')
        return
      }
      await requestCode(address)
      setNote(`We sent a code to ${address}.`)
      setStep('code')
    } catch (e) { fail(e) } finally { setBusy(false) }
  }

  async function sendNew() {
    setBusy(true); setError('')
    try {
      await requestCode(email.trim(), true)
      setNote(`We sent a new code to ${email.trim()}. The earlier one no longer works.`)
      setCode(''); setStep('code')
    } catch (e) { fail(e) } finally { setBusy(false) }
  }

  async function signIn(event: FormEvent) {
    event.preventDefault(); setError(''); setBusy(true)
    try {
      await verifyCode(email.trim(), code.trim())
      window.location.replace(consumeReturnTo() ?? '/')
    } catch (e) { fail(e) } finally { setBusy(false) }
  }

  return (
    <main className="page" style={{ maxWidth: 420, margin: '8vh auto' }}>
      <h1 style={{ marginBottom: 4 }}>{APP_NAME}</h1>
      <p className="muted small" style={{ marginBottom: 22 }}>
        Your books, in plain words. Sign in with the address your organisation knows you by.
      </p>

      {error ? <div className="card card-pad" role="alert"
                    style={{ borderLeft: '3px solid var(--danger)', marginBottom: 14 }}>{error}</div> : null}
      {note && !error ? <p className="small muted" style={{ marginBottom: 14 }}>{note}</p> : null}

      {step === 'email' ? (
        <form onSubmit={sendCode} className="stack" style={{ gap: 12 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Work email</span>
            <input className="input" type="email" required autoFocus autoComplete="email"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy || !email.trim()}>
            {busy ? 'Just a moment…' : 'Email me a code'}
          </button>
        </form>
      ) : step === 'choose' ? (
        <div className="stack" style={{ gap: 12 }}>
          <p className="small">
            We already emailed a code to <b>{email.trim()}</b>
            {minutesLeft ? ` — it still works for ${minutesLeft} more minute${minutesLeft === 1 ? '' : 's'}` : ''}.
          </p>
          <button className="btn btn-primary" onClick={() => {
            setNote(`Use the code we emailed to ${email.trim()} earlier.`); setStep('code')
          }}>Use the code I already have</button>
          <button className="btn" onClick={sendNew} disabled={busy}>Email me a new code</button>
        </div>
      ) : (
        <form onSubmit={signIn} className="stack" style={{ gap: 12 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Code from your email</span>
            <input className="input" required autoFocus inputMode="numeric" autoComplete="one-time-code"
                   value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy || !code.trim()}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <button className="btn btn-quiet btn-sm" type="button"
                  onClick={() => { setStep('email'); setCode(''); setNote(''); setError('') }}>
            Use a different address
          </button>
        </form>
      )}
    </main>
  )
}
